// src/App.js
import React, { useEffect, Suspense, lazy } from 'react';
import ReactGA from "react-ga4";
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { useMediaQuery } from "react-responsive";
import './App.css';

import { UserProvider } from "./contexts/UserContext";
import { MatchProvider } from "./contexts/MatchContext";
import { AchievementProvider } from "./contexts/AchievementContext";
import { AdminProvider } from "./contexts/AdminContext";
import AchievementPopup from "./components/AchievementPopup";

import Sidebar from './components/Sidebar';
import MobileHeader from './components/MobileHeader';

// Lazy load components
const QuizGame = lazy(() => import('./components/QuizGame'));
const Custom = lazy(() => import('./components/Custom'));
const PrivacyPolicy = lazy(() => import('./components/privacyPolicy'));
const DailyQuiz = lazy(() => import('./components/DailyQuiz'));
const FlagQuiz = lazy(() => import('./components/FlagQuiz'));
const Stats = lazy(() => import('./components/Stats'));
const PasswordReset = lazy(() => import('./components/ResetPassword'));
const Profile = lazy(() => import('./components/Profile'));
const CountryQuiz = lazy(() => import('./components/CountryQuiz'));
const PlayRanked = lazy(() => import('./components/PlayRanked'));
const MatchPage = lazy(() => import('./components/MatchPage'));
const EditProfile = lazy(() => import('./components/EditProfile'));
const Register = lazy(() => import('./components/Registrieren'));
const Login = lazy(() => import('./components/Login'));
const JoinFriendMatch = lazy(() => import('./components/JoinFriendMatch'));
const CombinedLeaderboard = lazy(() => import('./components/CombinedLeaderboard'));
const Contact = lazy(() => import('./components/Contact'));
const Achievements = lazy(() => import('./components/Achievements'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

const Articles = lazy(() => import('./components/ArticlesHub'));
const ImproveArticle = lazy(() => import('./components/Articles/HowToImprove'));
const ShouldKnowArticle = lazy(() => import('./components/Articles/EssentialTriviaKnowledge'));
const HistoryArticle = lazy(() => import('./components/Articles/History'));

const App = () => {
  const isWideScreen = useMediaQuery({ query: '(min-width: 1200px)' });
  const isMediumScreen = useMediaQuery({ query: '(min-width: 768px) and (max-width: 1199px)' });
  const isNarrowScreen = useMediaQuery({ query: '(max-width: 767px)' });
  
  const getScreenSize = () => {
    if (isWideScreen) return "wide";
    if (isMediumScreen) return "medium";
    return "narrow";
  };

  useEffect(() => {
    ReactGA.initialize("G-F4LJNXNP37");
    ReactGA.send("pageview");
  }, []);

  return (
    <Router>
      <UserProvider>
        <MatchProvider>
          <AchievementProvider>
            <AdminProvider>
              {(isWideScreen || isMediumScreen) && <Sidebar />}
              {isNarrowScreen && <MobileHeader />}
              <AchievementPopup />

              <div className={`complete-container ${getScreenSize()}`}>
                <Suspense fallback={<div></div>}>
                  <Routes>
                    <Route path="/" element={<QuizGame />} />
                    <Route path="/daily-quiz" element={<QuizGame />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/custom" element={<Custom />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/daily" element={<DailyQuiz />} />
                    <Route path="/flag-quiz" element={<FlagQuiz />} />
                    <Route path="/stats/:username" element={<Stats />} />
                    <Route path="/reset-password" element={<PasswordReset />} />
                    <Route path="/profile/:username" element={<Profile />} />
                    <Route path="/edit-profile" element={<EditProfile />} />
                    <Route path="/country-quiz/:continentId?" element={<CountryQuiz />} />
                    <Route path="/battle" element={<PlayRanked />} />
                    <Route path="/match/:matchId" element={<MatchPage />} />
                    <Route path="/articles" element={<Articles />} />
                    <Route path="/article/how-to-get-better-at-trivia" element={<ImproveArticle />} />
                    <Route path="/article/essential-trivia-knowledge" element={<ShouldKnowArticle />} />
                    <Route path="/article/history-of-trivia" element={<HistoryArticle />} />
                    <Route path="/join-friend-match/:matchKey" element={<JoinFriendMatch />} />
                    <Route path="/leaderboard" element={<CombinedLeaderboard />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/achievements/:username" element={<Achievements />} />
                    <Route path="/admin" element={<AdminPanel />} />
                  </Routes>
                </Suspense>
              </div>
            </AdminProvider>
          </AchievementProvider>
        </MatchProvider>
      </UserProvider>
    </Router>
  );
};

export default App;
